import { MaterialIcons } from "@expo/vector-icons";
import { useCallback, useContext, useMemo, useRef } from "react";
import { StyleSheet, TouchableWithoutFeedback, View } from "react-native";
import DraggableFlatList, {
  RenderItemParams,
  ScaleDecorator,
} from "react-native-draggable-flatlist";
import { Button, useTheme } from "react-native-elements";
import { PlayerContext, PlayerContextValue } from "../../backend/PlayerContext";
import { Spell } from "../../backend/spells/Spell";
import {
  getFavoriteSpells,
  removeFromFavoriteSpells,
} from "../../backend/spells/Spells";
import CategoryContainer from "../utility/CategoryContainer";
import { SpellView } from "../utility/SpellView";

function FavoriteSpellsContainerInside(props: {
  playerContext: PlayerContextValue;
}) {
  // TODO FIX FOR RENAMING SOUL SPLICE
  const favoriteSpells = getFavoriteSpells(props.playerContext).filter(
    (spell) => spell != null,
  );
  if (favoriteSpells.length === 0) {
    return null;
  }
  const playerContextRef = useRef(props.playerContext);
  playerContextRef.current = props.playerContext;

  const theme = useTheme();

  const renderItem = useCallback(
    ({ item, drag, isActive }: RenderItemParams<Spell>) => {
      const spell = item;
      return (
        <ScaleDecorator>
          <View style={styles.spellViewContainer} key={spell.getName()}>
            <View
              importantForAccessibility="no-hide-descendants"
              accessibilityElementsHidden={true}
              style={styles.dragHandle}
            >
              <TouchableWithoutFeedback
                onPressIn={drag}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <MaterialIcons
                  name="drag-indicator"
                  size={24}
                  color={theme.theme.colors.grey3}
                />
              </TouchableWithoutFeedback>
            </View>
            <SpellView key={spell.getName()} spell={spell} isMinimal={true} />
            <Button
              icon={<MaterialIcons name="close" size={24} color="white" />}
              accessibilityLabel="Remove from Favorites"
              containerStyle={styles.deleteButton}
              style={styles.deleteButtonInner}
              iconContainerStyle={styles.deleteButtonInner}
              buttonStyle={styles.deleteButtonInner}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              onPress={() =>
                playerContextRef.current.apply((state) =>
                  removeFromFavoriteSpells(state, spell),
                )
              }
            />
          </View>
        </ScaleDecorator>
      );
    },
    [removeFromFavoriteSpells, theme],
  );
  const keyExtractor = useCallback((spell: Spell) => spell.getName(), []);
  return useMemo(
    () => (
      <View style={{ flex: 1 }}>
        <CategoryContainer
          title="Favorites"
          containerStyle={styles.categoryContainer}
          contentContainerStyle={{ flex: 1 }}
          disableExpand={true}
        >
          <DraggableFlatList
            data={favoriteSpells}
            renderItem={renderItem}
            containerStyle={{ flex: 1 }}
            keyExtractor={keyExtractor}
            onDragEnd={(params) => {
              playerContextRef.current.apply((state) => {
                state.favoriteSpells = params.data.map((spell) =>
                  spell.getName(),
                );
                return state;
              });
            }}
          />
        </CategoryContainer>
      </View>
    ),
    [favoriteSpells.map((spell) => spell.getName()).join()],
  );
}

export function FavoriteSpellsContainer(props: { isVisible?: boolean }) {
  const playerContext = useContext(PlayerContext);

  return useMemo(
    () => (
      <FavoriteSpellsContainerInside playerContext={playerContext} {...props} />
    ),
    [props.isVisible !== false ? playerContext : false],
  );
}

const styles = StyleSheet.create({
  spellViewContainer: {
    paddingTop: 8,
    flexDirection: "row",
    alignItems: "center",
  },
  deleteButton: {
    marginLeft: 8,
  },
  deleteButtonInner: {
    paddingHorizontal: 0,
    paddingVertical: 0,
    height: 32,
    width: 32,
    borderRadius: 16,
  },
  categoryContainer: {
    paddingTop: 8,
    flex: 1,
  },
  dragHandle: {
    cursor: "grab",
    marginRight: 8,
  },
});
